"use client";

import { useEffect, useRef, useState, type FormEvent } from "react";
import { Button } from "@/components/ui/Button";
import { getContactEmail } from "@/lib/contact/email";
import { decodeEmailPayload } from "@/lib/contact/obfuscate";

type ContactFormProps = {
  fields: {
    name: string;
    email: string;
    company: string;
    message: string;
  };
  submitLabel: string;
  /** XOR-encoded inbox for static-export mailto fallback. */
  emailPayload?: number[];
};

type FieldErrors = Partial<
  Record<"name" | "email" | "company" | "message", string>
>;

type FormStatus = "idle" | "success" | "error";

const EMAIL_PATTERN = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

/** Static Apache/PHP endpoint (copied from `public/api/` into the export). */
const CONTACT_API_URL =
  process.env.NEXT_PUBLIC_CONTACT_API_URL?.trim() || "/api/contact.php";

const inputClassName =
  "w-full border-0 border-b border-white/15 bg-transparent px-0 py-3.5 text-foreground outline-none transition-colors placeholder:text-muted/50 focus:border-white/40";

function validate(formData: FormData): FieldErrors {
  const name = formData.get("name")?.toString().trim() ?? "";
  const email = formData.get("email")?.toString().trim() ?? "";
  const message = formData.get("message")?.toString().trim() ?? "";
  const fieldErrors: FieldErrors = {};

  if (!name) fieldErrors.name = "Name is required.";
  if (!email) {
    fieldErrors.email = "Email is required.";
  } else if (!EMAIL_PATTERN.test(email)) {
    fieldErrors.email = "Enter a valid email address.";
  }
  if (!message) fieldErrors.message = "Please tell us about your project.";

  return fieldErrors;
}

function submitViaMailto(
  inbox: string,
  name: string,
  email: string,
  company: string,
  body: string,
) {
  const subject = encodeURIComponent(`Project inquiry from ${name}`);
  const mailBody = encodeURIComponent(
    [`Name: ${name}`, `Email: ${email}`, company ? `Company: ${company}` : "", "", body]
      .filter(Boolean)
      .join("\n"),
  );
  window.location.href = `mailto:${inbox}?subject=${subject}&body=${mailBody}`;
}

export function ContactForm({
  fields,
  submitLabel,
  emailPayload,
}: ContactFormProps) {
  const [status, setStatus] = useState<FormStatus>("idle");
  const [message, setMessage] = useState<string | undefined>();
  const [fieldErrors, setFieldErrors] = useState<FieldErrors>({});
  const [pending, setPending] = useState(false);
  const [inbox, setInbox] = useState<string | undefined>();
  const startedRef = useRef<number>(Math.floor(Date.now() / 1000));

  useEffect(() => {
    startedRef.current = Math.floor(Date.now() / 1000);
    if (emailPayload?.length) {
      setInbox(decodeEmailPayload(emailPayload));
    } else {
      setInbox(getContactEmail());
    }
  }, [emailPayload]);

  if (status === "success") {
    return (
      <div className="py-4" role="status" aria-live="polite">
        <p className="text-lg font-medium text-foreground">{message}</p>
        <p className="mt-3 text-sm text-muted">
          We will follow up at the email address you provided.
        </p>
      </div>
    );
  }

  async function handleSubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    const form = event.currentTarget;
    const formData = new FormData(form);
    const errors = validate(formData);

    if (Object.keys(errors).length > 0) {
      setStatus("error");
      setMessage("Please fix the highlighted fields.");
      setFieldErrors(errors);
      return;
    }

    setPending(true);
    setFieldErrors({});
    setStatus("idle");
    setMessage(undefined);

    const name = formData.get("name")?.toString().trim() ?? "";
    const email = formData.get("email")?.toString().trim() ?? "";
    const company = formData.get("company")?.toString().trim() ?? "";
    const body = formData.get("message")?.toString().trim() ?? "";
    const website = formData.get("website")?.toString() ?? "";

    const payload = {
      name,
      email,
      company,
      message: body,
      website,
      started: startedRef.current,
    };

    try {
      const response = await fetch(CONTACT_API_URL, {
        method: "POST",
        headers: { "Content-Type": "application/json", Accept: "application/json" },
        body: JSON.stringify(payload),
      });

      const data = (await response.json().catch(() => null)) as
        | { ok?: boolean; message?: string; error?: string }
        | null;

      if (response.ok && data?.ok) {
        setStatus("success");
        setMessage(
          data.message ||
            "Thank you — we received your message and will respond within one business day.",
        );
        form.reset();
        startedRef.current = Math.floor(Date.now() / 1000);
        return;
      }

      // PHP unavailable (static preview / local) — fall back to mailto.
      if (response.status === 404 || response.status === 405) {
        if (inbox) {
          submitViaMailto(inbox, name, email, company, body);
          setStatus("success");
          setMessage(
            "Thank you — your email client should open with the message ready to send.",
          );
          form.reset();
          return;
        }
      }

      setStatus("error");
      setMessage(
        data?.error ||
          "Unable to send your message right now. Please try again or email office@insightfeatures.com.",
      );
    } catch {
      if (inbox) {
        submitViaMailto(inbox, name, email, company, body);
        setStatus("success");
        setMessage(
          "Thank you — your email client should open with the message ready to send.",
        );
        form.reset();
      } else {
        setStatus("error");
        setMessage(
          "Unable to send your message right now. Please email office@insightfeatures.com.",
        );
      }
    } finally {
      setPending(false);
    }
  }

  return (
    <form className="relative space-y-8" onSubmit={handleSubmit} noValidate>
      {status === "error" && message ? (
        <p className="text-sm text-foreground" role="alert">
          {message}
        </p>
      ) : null}

      {/* Honeypot — leave empty. Hidden from users, filled by many bots. */}
      <div className="pointer-events-none absolute left-[-10000px] top-auto h-px w-px overflow-hidden opacity-0" aria-hidden="true">
        <label htmlFor="website">Website</label>
        <input
          id="website"
          name="website"
          type="text"
          tabIndex={-1}
          autoComplete="off"
        />
      </div>

      <div>
        <label htmlFor="name" className="mb-2 block text-sm text-muted">
          {fields.name}
        </label>
        <input
          id="name"
          name="name"
          type="text"
          required
          autoComplete="name"
          aria-invalid={Boolean(fieldErrors.name)}
          aria-describedby={fieldErrors.name ? "name-error" : undefined}
          className={inputClassName}
        />
        {fieldErrors.name ? (
          <p id="name-error" className="mt-2 text-sm text-muted">
            {fieldErrors.name}
          </p>
        ) : null}
      </div>

      <div>
        <label htmlFor="email" className="mb-2 block text-sm text-muted">
          {fields.email}
        </label>
        <input
          id="email"
          name="email"
          type="email"
          required
          autoComplete="email"
          aria-invalid={Boolean(fieldErrors.email)}
          aria-describedby={fieldErrors.email ? "email-error" : undefined}
          className={inputClassName}
        />
        {fieldErrors.email ? (
          <p id="email-error" className="mt-2 text-sm text-muted">
            {fieldErrors.email}
          </p>
        ) : null}
      </div>

      <div>
        <label htmlFor="company" className="mb-2 block text-sm text-muted">
          {fields.company}
        </label>
        <input
          id="company"
          name="company"
          type="text"
          autoComplete="organization"
          className={inputClassName}
        />
      </div>

      <div>
        <label htmlFor="message" className="mb-2 block text-sm text-muted">
          {fields.message}
        </label>
        <textarea
          id="message"
          name="message"
          rows={5}
          required
          aria-invalid={Boolean(fieldErrors.message)}
          aria-describedby={fieldErrors.message ? "message-error" : undefined}
          className={`${inputClassName} resize-y`}
        />
        {fieldErrors.message ? (
          <p id="message-error" className="mt-2 text-sm text-muted">
            {fieldErrors.message}
          </p>
        ) : null}
      </div>

      <Button type="submit" disabled={pending}>
        {pending ? "Sending…" : submitLabel}
      </Button>
    </form>
  );
}
